import React from 'react';
import Badge from '../../common/Badge';
import { formatDate } from './mockData';

// Helper functions
const getStatusVariant = (status) => {
  switch (status) {
    case 'published':
      return 'success';
    case 'draft':
      return 'warning';
    case 'archived':
      return 'default';
    default:
      return 'default';
  }
};

const getStatusLabel = (status) => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const getPageTypeVariant = (pageType) => {
  switch ((pageType || '').toUpperCase()) {
    case 'STORYTELLING':
      return 'info';
    case 'STANDARD':
    default:
      return 'default';
  }
};

const getPageTypeLabel = (pageType) => {
  switch ((pageType || '').toUpperCase()) {
    case 'STORYTELLING':
      return 'Story';
    case 'STANDARD':
    default:
      return 'Standard';
  }
};

const canDelete = (status) => {
  return status === 'draft' || status === 'archived';
};

/**
 * Creates the column definitions for the Lead Magnet List table
 * @param {Object} handlers - Object containing handler functions
 * @param {Function} handlers.onEdit - Handler for edit action
 * @param {Function} handlers.onCopy - Handler for the duplicate / copy action
 * @param {Function} handlers.onDelete - Handler for delete action
 * @param {Function} handlers.onPublish - Handler for publish action
 * @returns {Array} Array of column definitions
 */
export const createLeadMagnetColumns = ({ onEdit, onCopy, onDelete, onPublish }) => [
  {
    key: 'internalName',
    header: 'Name',
    render: (value, row) => (
      <div className="lead-magnet-list__name-cell">
        <strong>{row.internalName}</strong>
        {row.pageTitle && (
          <span className="lead-magnet-list__page-title">
            {row.pageTitle}
          </span>
        )}
      </div>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (value, row) => (
      <Badge variant={getStatusVariant(row.status)} size="small">
        {getStatusLabel(row.status)}
      </Badge>
    ),
  },
  {
    key: 'pageType',
    header: 'Type',
    render: (value, row) => (
      <Badge variant={getPageTypeVariant(row.pageType)} size="small">
        {getPageTypeLabel(row.pageType)}
      </Badge>
    ),
  },
  {
    key: 'slug',
    header: 'Slug/URL',
    render: (value, row) => {
      if (!row.slug) {
        return <span className="lead-magnet-list__no-slug">Not published</span>;
      }
      const fullUrl = `${window.location.origin}/lm/${row.slug}`;
      const handleCopy = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(fullUrl);
      };
      return (
        <span className="lead-magnet-list__slug-cell">
          <a
            href={`/lm/${row.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="lead-magnet-list__slug"
            onClick={(e) => e.stopPropagation()}
          >
            /lm/{row.slug}
          </a>
          <button
            className="lead-magnet-list__btn lead-magnet-list__btn--copy"
            onClick={handleCopy}
            title="Copy full URL"
          >
            Copy
          </button>
        </span>
      );
    },
  },
  {
    key: 'createdDate',
    header: 'Created Date',
    render: (value, row) => formatDate(row.createdDate),
  },
  {
    key: 'lastUpdated',
    header: 'Last Updated',
    render: (value, row) => formatDate(row.lastUpdated),
  },
  {
    key: 'actions',
    header: 'Actions',
    render: (value, row) => (
      <div className="lead-magnet-list__actions">
        {onEdit && (
          <button
            className="lead-magnet-list__btn lead-magnet-list__btn--edit"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(row);
            }}
            title="Edit"
          >
            Edit
          </button>
        )}
        {onCopy && (
          <button
            className="lead-magnet-list__btn lead-magnet-list__btn--duplicate"
            onClick={(e) => {
              e.stopPropagation();
              onCopy(row);
            }}
            title="Duplicate this lead magnet as a new draft"
          >
            Copy
          </button>
        )}
        {onPublish && row.status !== 'published' && (
          <button
            className="lead-magnet-list__btn lead-magnet-list__btn--publish"
            onClick={(e) => {
              e.stopPropagation();
              onPublish(row);
            }}
            title="Publish"
          >
            Publish
          </button>
        )}
        {onDelete && canDelete(row.status) && (
          <button
            className="lead-magnet-list__btn lead-magnet-list__btn--delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(row);
            }}
            title="Delete"
          >
            Delete
          </button>
        )}
        {!onEdit && !onPublish && !onDelete && (
          <span className="lead-magnet-list__no-actions">No actions available</span>
        )}
      </div>
    ),
  },
];

